import type {
  SignupMetadata,
  LoginMetadata,
  DeviceInfo,
} from "./utils/deviceInfo";

export enum ActivityCategory {
  Movies = "movies",
  Dining = "dining",
  Games = "games",
  Outdoor = "outdoor",
  Drinks = "drinks",
  Fitness = "fitness",
  General = "general",
}

export interface User {
  id: string;
  username: string;
  email: string;
  profileImageUrl: string;
  location: string;
  isAccountVerifiedForBadge: boolean; // For verified badge (blue checkmark) - NOT email verification
  bio?: string;
  interests?: string[];
  isOnline?: boolean;
  followers?: string[];
  following?: string[];
  pendingFollowers?: string[]; // Incoming requests
  requestedFollowing?: string[]; // Outgoing requests
  hasCompletedOnboarding?: boolean;
  passedActivityIds?: string[];
  personaTitle?: string;
  // New fields for Profile Builder
  dateOfBirth?: string;
  gender?: "male" | "female" | "other" | "undisclosed";
  passions?: string[];
  // Push notification fields
  fcmTokens?: string[]; // Changed to array to support multiple devices
  fcmTokenUpdatedAt?: string;
  // AI-generated user profile for hyper-personalization
  userProfile?: UserProfile;
  // Account linking banner
  dismissedPrivateRelayBanner?: boolean;
  // Timestamps
  createdAt?: string; // ISO 8601 format - when user first signed up
  updatedAt?: string; // ISO 8601 format - when user data was last modified
  lastLoginAt?: string; // ISO 8601 format - when user last logged in
  lastActiveAt?: string; // ISO 8601 format - when user was last active (used for "last seen")

  // Signup/Login Metadata
  signupMethod?: SignupMetadata["signupMethod"];
  signupMetadata?: SignupMetadata; // Device, location, source info at signup
  lastLoginMetadata?: LoginMetadata; // Device, session info from last login
  loginHistory?: LoginMetadata[]; // Optional: Track recent logins (limit to last 10)

  // Location tracking (updated on login)
  lastKnownCountry?: string;
  lastKnownCity?: string;
  lastKnownTimezone?: string;

  // Last known coordinates for proximity matching
  lastKnownCoordinates?: { lat: number; lng: number };
  lastKnownCoordinatesUpdatedAt?: string;

  // Notification preferences
  notificationSettings?: {
    matchSuggestionsEnabled?: boolean;
    matchSuggestionsMaxPerDay?: number;
    backgroundLocationEnabled?: boolean;
  };

  // Search history for AI context refinement (limit to last 50 searches)
  searchHistory?: string[];
}

export interface UserProfile {
  summary: string; // AI-generated natural language summary of the user
  preferences: {
    categories: { name: string; score: number }[]; // Activity categories with engagement scores
    locations: { area: string; frequency: number }[]; // Most visited areas
    timePreferences: {
      morning: number; // 6am-12pm
      afternoon: number; // 12pm-6pm
      evening: number; // 6pm-10pm
      night: number; // 10pm-6am
    };
    groupSizePreference: "intimate" | "medium" | "large" | "flexible"; // Preferred group dynamics
    role: "organizer" | "joiner" | "balanced"; // Activity creation vs joining behavior
    activityStyle: string; // Spontaneous, planner, flexible, etc
  };
  insights: {
    socialBehavior: string; // How they interact with others (selective, open, strategic)
    activityPatterns: string; // What they create/join, frequency, commitment level
    uniqueTraits: string; // Distinctive characteristics that define them
    commitmentLevel: string; // How reliable they are once they commit
    engagementStyle: string; // Communication style, spontaneity, planning approach
  };
  // Tracking for incremental updates
  analyzedActivityIds: string[]; // Activities already analyzed in this profile
  pendingActions: UserAction[]; // Actions not yet processed
  lastUpdated: string; // ISO 8601 format
  version: number; // Profile schema version for migrations
}

export interface UserAction {
  type:
    | "create"
    | "join"
    | "decline"
    | "leave"
    | "delete"
    | "pass"
    | "update"
    | "kick"
    | "follow_request"
    | "follow_accept"
    | "unfollow"
    | "cancel_follow_request"
    | "decline_follow_request"
    | "remove_follower"
    | "mutual_match"
    | "message"
    | "message_reaction"
    | "story_post"
    | "story_view"
    | "activity_view"
    | "search_query"
    | "notification_click"
    | "ai_chat_message"
    | "ai_idea_generate";
  activityId?: string;
  activityTitle?: string;
  category?: string;
  location?: string;
  timestamp: string;
  metadata?: Record<string, any>; // For social actions (targetUserId, chatId, etc.), view duration, search terms, notification types, AI messages, generated ideas
}

export interface ActivityRequest {
  id: string;
  creator: User; // The full user object for the creator
  createdAt: string; // ISO 8601 format
  updatedAt?: string; // ISO 8601 format - when activity was last modified
  category: ActivityCategory;
  title: string;
  description: string | null;
  location: string;
  coordinates?: { lat: number; lng: number }; // Added field for geocoded location
  dateTime: string; // ISO 8601 format
  duration: number; // Duration in minutes
  groupSize: number;
  currentSize: number;
  isActive: boolean | true;
  isDeleted?: boolean | false;
  deletedAt?: string | null; // ISO 8601 format when activity was deleted
  isReported?: boolean | false; // Indicates if the activity has been reported
  isFull?: boolean | false; // Indicates if the activity is full (client and backend)
  fullAt?: string | null; // ISO 8601 format when activity became full
  isExpired?: boolean | false; // Indicates if the activity is expired (client and backend)
  expiredAt?: string | null; // ISO 8601 format when activity became expired
  isTrending?: boolean | false;
  trendedAt?: string | null; // ISO 8601 format when activity became trending
  imageUrl?: string | null;
  participants: User[]; // The backend sends a list of full user objects
  chatId: string;
  reason?: string; // AI-generated reason for recommendation
  participantIds?: string[]; // Optional array of participant IDs for easier access
  kickedParticipantIds?: string[]; // Optional array of kicked participant IDs for easier access
  pendingRejoinRequests?: string[]; // Users who were kicked and are requesting to rejoin
  creatorId: string; // ID of the creator for quick reference
  isPrivate?: boolean | false; // Indicates if the activity is private (not shown in feed/map)
  invitedUserIds?: string[]; // Array of user IDs who are invited to this private activity
  pendingInvites?: string[]; // Array of user IDs with pending invitations
}

export interface VibeCheckUser {
  user: User;
  vibe: {
    score: number;
    summary: string;
    one_liner: string;
  };
}

export interface AIMessage {
  role: "user" | "model" | "system";
  content: string | ParsedAIResponse;
  timestamp: string;
  isTyping?: boolean;
}

export interface ParsedAIResponse {
  category?: string;
  title?: string;
  description?: string;
  location?: string;
  placeId?: string; // Add placeId for Google Places
  coordinates?: { lat: number; lng: number };
  suggestedDateTime?: string;
  duration?: number | string; // AI might provide string, converted to minutes (number)
  groupSize?: number;
  imageUrl?: string; // Activity photo URL
  followUpQuestions?: string[];
  suggestedReplies?: string[];
  hasConfirmedDefaults?: boolean; // Track if user has confirmed default values
  isPrivate?: boolean; // Whether activity is private
}

export interface MatchProposal {
  id: string;
  ownerUserId: string;
  candidateUserIds: string[];
  activityDraft: ParsedAIResponse;
  status: "pending" | "accepted" | "declined" | "created" | "expired";
  createdAt: string;
  expiresAt?: string;
  createdActivityId?: string;
  proximityKm?: number;
}

export interface GroundingChunk {
  web?: {
    uri?: string;
    title?: string;
  };
}

export interface ActivityIdea {
  title: string; // e.g., "Drinks at Hooters - Free flow from 7pm to 9pm"
  description: string; // Detailed description of the activity
  sources: GroundingChunk[]; // Web links or platform activities
  type: "web" | "platform"; // Whether from web search or existing activities
  activityId?: string; // If type is 'platform', the activity ID
  prompt: string; // The full context to send to AI for creating draft
}

export interface SearchAIResponse {
  ideas: ActivityIdea[];
}

export interface MessageReaction {
  emoji: string;
  userId: string;
  timestamp: string;
}

export interface ChatMediaAttachment {
  id: string;
  type: "image" | "video" | "audio" | "document";
  url: string;
  mimeType: string;
  fileName: string;
  size: number;
}

export interface ChatLocationPayload {
  kind: "static" | "live";
  latitude: number;
  longitude: number;
  label?: string;
  placeName?: string;
  accuracyMeters?: number;
  sharedAt: string;
  liveShareId?: string;
  expiresAt?: string;
}

export interface ChatMessage {
  id: string;
  clientRequestId?: string;
  senderId: string;
  text: string;
  format?: "plain" | "whatsapp";
  timestamp: string;
  media?: ChatMediaAttachment[];
  location?: ChatLocationPayload;
  editedAt?: string;
  deletedForEveryone?: boolean;
  deletedForUsers?: string[];
  replyTo?: {
    messageId: string;
    senderId: string;
    senderUsername: string;
    text: string;
  };
  status?: "sending" | "failed" | "sent" | "delivered" | "read";
  readBy?: string[]; // Array of user IDs who have read this message
  deliveredTo?: string[]; // Array of user IDs who have received this message
  readAt?: string; // Timestamp when first read
  deliveredAt?: string; // Timestamp when first delivered
  reactions?: MessageReaction[]; // Array of reactions to this message
  isForwarded?: boolean;
  starredBy?: string[];
}

export interface Chat {
  id: string;
  activityId: string | null;
  activityTitle: string;
  activityImageUrl?: string;
  activityStatus?: "active" | "inactive" | "unknown";
  participants: string[];
  leftParticipantIds?: string[];
  typing?: Record<string, string>;
  messages: ChatMessage[];
}

export interface Notification {
  id: string;
  userId: string;
  type:
    | "JOIN"
    | "MESSAGE"
    | "LEAVE"
    | "FOLLOW_REQUEST"
    | "FOLLOW_ACCEPT"
    | "FOLLOW_DECLINE"
    | "ACTIVITY_UPDATE"
    | "ACTIVITY_DELETE"
    | "NEW_STORY"
    | "ACTIVITY_KICK"
    | "MUTUAL_MATCH"
    | "ACTIVITY_FULL"
    | "DIRECT_CHAT"
    | "FOLLOWER_REMOVED"
    | "GROUP_MESSAGE"
    | "DIRECT_MESSAGE"
    | "ACTIVITY_REMINDER"
    | "STORY_VIEW"
    | "STORY_REACT"
    | "ACCOUNT_DELETION"
    | "REJOIN_REQUEST"
    | "REJOIN_APPROVED"
    | "REJOIN_DECLINED"
    | "ACTIVITY_INVITE"
    | "ACTIVITY_INVITE_ACCEPT"
    | "ACTIVITY_INVITE_DECLINE"
    | "ACTIVITY_INVITE_CANCEL"
    | "MATCH_SUGGESTION";
  text: string;
  link: string;
  isRead: boolean;
  createdAt: string;
  fromUser: User;
  chatId: string;
  activityId?: string; // For activity-related notifications
  fromUserId: string; // For sender tracking
  count?: number; // Number of grouped similar notifications
  proposalId?: string; // For match suggestion proposals
}

export type AuditLogType =
  | "ACTIVITY_CREATE"
  | "ACTIVITY_JOIN"
  | "ACTIVITY_LEAVE"
  | "ACTIVITY_PASS"
  | "ACTIVITY_DELETE"
  | "ACTIVITY_RESTORE"
  | "ACTIVITY_UPDATE"
  | "ACTIVITY_KICK_PARTICIPANT"
  | "ACTIVITY_KICKED"
  | "PROFILE_UPDATE"
  | "FOLLOW_USER"
  | "UNFOLLOW_USER"
  | "GOT_FOLLOWER"
  | "FOLLOW_REQUEST_SENT"
  | "FOLLOW_REQUEST_DECLINED"
  | "FOLLOW_REQUEST_CANCELLED"
  | "USER_JOINED_APP"
  | "USER_DELETED_ACCOUNT"
  | "USER_REPORTED"
  | "USER_BANNED"
  | "USER_UNBANNED"
  | "STORY_POSTED"
  | "STORY_DELETED"
  | "MESSAGE_SENT"
  | "MESSAGE_DELETED"
  | "CHAT_CREATED"
  | "CHAT_DELETED"
  | "STORY_UPLOAD"
  | "STORY_VIEW"
  | "NOTIFICATION_SENT"
  | "NOTIFICATION_READ"
  | "PASSWORD_CHANGED"
  | "EMAIL_CHANGED"
  | "LOGIN_SUCCESS"
  | "LOGIN_FAILURE"
  | "SIGNUP"
  | "LOGOUT"
  | "PASSWORD_RESET_REQUESTED"
  | "PASSWORD_RESET_COMPLETED"
  | "ACCOUNT_VERIFIED"
  | "ACCOUNT_SUSPENDED"
  | "ACCOUNT_REACTIVATED"
  | "AI_PROFILE_GENERATED"
  | "AI_PROFILE_UPDATED"
  | "SEARCH_PERFORMED"
  | "ACTIVITY_IDEA_GENERATED"
  | "REPORT_SUBMITTED"
  | "REPORT_RESOLVED";

export interface AuditLog {
  id: string;
  userId: string;
  type: AuditLogType;
  text: string;
  link: string;
  createdAt: string;
  fromUser?: User;
}

export type UserActionType =
  | "create"
  | "join"
  | "decline"
  | "leave"
  | "delete"
  | "pass"
  | "update"
  | "kick"
  | "message"
  | "message_reaction"
  | "story_post"
  | "story_view"
  | "activity_view"
  | "search_query"
  | "notification_click"
  | "ai_chat_message"
  | "ai_idea_generate"
  | "follow_request"
  | "follow_accept"
  | "follow_decline"
  | "unfollow"
  | "remove_follower"
  | "cancel_follow_request"
  | "mutual_follow";
